import { WHALES_COLUMN, type ComparisonColumnId } from "@/lib/whales";
import { getActiveAdvancedFilterCount } from "@/components/optimizer/filters/advanced-filter-count";
import { resolveEffectiveSlipContext } from "@/components/optimizer/props-slip-breakevens";
import type { OptimizerQueryState } from "@/components/optimizer/query-state";
import type { DisplayPlayerProp, PlatformType } from "@/components/optimizer/utils-v2";
import { rowStakeQuote } from "@/components/optimizer/utils-v2/displayed-odds";
import { leagueLabel } from "@/components/odds-screen/live/view-model";
import { dayClock } from "@/components/odds-screen/live/time";
import { localTodayYmd, startTimeChoices, type TimeChoices } from "@/lib/game-date";
import type { Catalog, Dfs, EventHeader, EventId, EventRowsView, LeagueId } from "@/lib/odds-screen/live/types";
import { americanToDecimal, formatDecimalOdds, type OddsFormat } from "@/utils/calculators/odds-utils";
import type { GradingOf } from "@/lib/fantasy-props/rows";
import type { StakeQuote } from "@/lib/book-stake";
import { isUnderSlotSide } from "@/lib/selection-side";

const ESPORTS_LEAGUES = new Set(["csgo", "lol", "dota2", "valorant", "cod", "cs2", "r6"]);

export function isEsportsLeague(league: string): boolean {
  return ESPORTS_LEAGUES.has(league);
}

export type LeagueChoice = { readonly id: LeagueId; readonly label: string; readonly events: number; readonly catalogProps: number; readonly props: number | null; readonly active: boolean };

type FamilyPick = (family: { readonly kind: string; readonly rows: number }) => boolean;

function leagueChoices(catalog: Catalog | null, selected: readonly LeagueId[], counts: FamilyPick, esports = false): readonly LeagueChoice[] {
  if (catalog === null) return [];
  const choices: LeagueChoice[] = [];
  for (const id of catalog.leagueOrder) {
    const league = catalog.leagues.get(id);
    if (league === undefined || (!esports && isEsportsLeague(id)) || !league.families.some(counts)) continue;
    choices.push({
      id,
      label: leagueLabel(id),
      events: league.events,
      catalogProps: league.families.filter(counts).reduce((sum, family) => sum + family.rows, 0),
      props: null,
      active: selected.includes(id),
    });
  }
  return choices;
}

export function propsLeagues(catalog: Catalog | null, selected: readonly LeagueId[], esports = false): readonly LeagueChoice[] {
  return leagueChoices(catalog, selected, (family) => family.kind === "props", esports);
}

export function optimizerLeagues(catalog: Catalog | null, selected: readonly LeagueId[], esports = false): readonly LeagueChoice[] {
  return leagueChoices(catalog, selected, () => true, esports);
}

export type LeagueScope = {
  readonly selected: readonly LeagueId[];
  readonly single: LeagueId | null;
  readonly known: boolean;
  readonly knownSelected: readonly LeagueId[];
  readonly subscribed: readonly LeagueId[];
};

export function postingLeagues(choices: readonly LeagueChoice[]): readonly LeagueId[] {
  return choices.filter((choice) => choice.catalogProps > 0).map((choice) => choice.id);
}

export function leagueScope(picked: readonly LeagueId[], choices: readonly LeagueChoice[] | null, remembered: readonly LeagueId[] = [], live = false): LeagueScope {
  const selected = [...new Set(picked)];
  const ids = new Set((choices ?? []).map((choice) => choice.id));
  const knownSelected = selected.filter((id) => ids.has(id));
  const known = knownSelected.length === selected.length;
  const subscribed = choices === null
    ? selected.length === 0 ? remembered : selected
    : selected.length === 0 ? live ? choices.map((choice) => choice.id) : postingLeagues(choices) : known ? selected : [];
  return { selected, single: selected.length === 1 ? selected[0] : null, known, knownSelected, subscribed };
}

export function countedLeagues(leagues: readonly LeagueChoice[], view: EventRowsView<{ readonly league: LeagueId }>, subscribed: readonly LeagueId[]): LeagueChoice[] {
  const counts = new Map<LeagueId, number>();
  for (const row of view.rows.values()) counts.set(row.league, (counts.get(row.league) ?? 0) + 1);
  return leagues.map((league) => ({ ...league, props: subscribed.includes(league.id) ? counts.get(league.id) ?? 0 : null }));
}

export function timeChoices(view: EventRowsView<{ readonly eventId: EventId }>, todayYmd = localTodayYmd(), nowMs = Date.now(), live = false): TimeChoices {
  const perEvent = new Map<EventId, number>();
  for (const row of view.rows.values()) perEvent.set(row.eventId, (perEvent.get(row.eventId) ?? 0) + 1);
  const starts: [number, number][] = [];
  for (const [eventId, rows] of perEvent) {
    const event = view.events.get(eventId);
    if (event !== undefined) starts.push([event.startMs, rows]);
  }
  return startTimeChoices(starts, todayYmd, nowMs, live);
}

export type CellPrice = { readonly odds: number | null; readonly line: number | null; readonly stake: StakeQuote | null };

export function cellPrice(row: DisplayPlayerProp, book: PlatformType): CellPrice {
  const side = isUnderSlotSide(row.bet_type) ? "under" : "over";
  const record = row as unknown as Record<string, unknown>;
  const value = record[`${book}_${side}_odds`] ?? record[`${book}_odds`];
  const odds = typeof value === "number" && Number.isFinite(value) ? value : null;
  const line = row.__comparison_lines?.[book];
  return { odds, line: typeof line === "number" ? line : null, stake: rowStakeQuote(row, book, odds) };
}

export type BoardColumn = { readonly kind: "book"; readonly book: PlatformType } | { readonly kind: "whales" };

export type ColumnPreference = { readonly order: readonly ComparisonColumnId[]; readonly hidden: readonly ComparisonColumnId[] };

export function bookColumns(
  books: readonly string[],
  { order, hidden }: ColumnPreference,
  analyticalBookIds: readonly PlatformType[],
  platform?: PlatformType,
): BoardColumn[] {
  const present = new Set(books);
  const pending = new Set(analyticalBookIds.filter((book) => book !== platform && present.has(book) && !hidden.includes(book)));
  const ordered: BoardColumn[] = [];
  for (const id of order) {
    if (id === WHALES_COLUMN) {
      if (!hidden.includes(id)) ordered.push({ kind: "whales" });
    } else if (pending.delete(id)) ordered.push({ kind: "book", book: id });
  }
  for (const book of analyticalBookIds) if (pending.delete(book)) ordered.push({ kind: "book", book });
  return ordered;
}

export function columnBooks(columns: readonly BoardColumn[]): PlatformType[] {
  return columns.flatMap((column) => (column.kind === "book" ? [column.book] : []));
}

export function showsWhales(columns: readonly BoardColumn[]): boolean {
  return columns.some((column) => column.kind === "whales");
}

export function formatMultiple(value: number): string {
  return `${value.toFixed(2).replace(/\.?0+$/, "")}x`;
}

export function formatPct(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}

export function formatOdds(american: number | null, format: OddsFormat): string {
  if (american === null) return "";
  if (format === "american") return american > 0 ? `+${american}` : `${american}`;
  return formatDecimalOdds(americanToDecimal(american), format);
}

export function slipNeed(platform: PlatformType, slipType: string, boostPct = 0, league: string | null = null): number | null {
  const context = resolveEffectiveSlipContext({ platform, rawSlipTypeId: slipType || null, boostPct, league });
  const breakeven = context.effectiveBreakevenPct;
  return breakeven != null && breakeven > 0 ? breakeven : null;
}

export function shownStateOf(state: OptimizerQueryState, book: PlatformType | null): OptimizerQueryState {
  return book === null || book === state.platform ? state : { ...state, sportsbook: book, platform: book, slipType: "" };
}

export function appNeedOf(
  state: OptimizerQueryState,
  book: PlatformType | null,
  sanitize: (next: OptimizerQueryState) => OptimizerQueryState,
  single: LeagueId | null,
  cardView: boolean,
  gradingOf: GradingOf,
): (app: PlatformType, league: LeagueId) => number | null {
  return (app, league) => {
    if (gradingOf(app).kind === "ungraded") return null;
    const next = app === book ? shownStateOf(state, book) : sanitize({ ...state, sportsbook: app, platform: app });
    return slipNeed(app, next.slipType, 0, league) ?? (cardView ? slipNeed(app, "", 0, null) : slipNeed(app, next.slipType, 0, single));
  };
}

export type AppPlays = { readonly count: number; readonly atLeast: boolean } | "ungraded";

export function appPlays<A extends string>(
  dfs: Dfs,
  leagues: readonly LeagueId[],
  apps: readonly A[],
  needOf: (app: A, league: LeagueId) => number | null,
): ReadonlyMap<A, AppPlays | null> {
  return new Map(apps.map((app) => {
    let count = 0;
    let atLeast = false;
    let counted = false;
    let posted = false;
    for (const league of leagues) {
      const entry = dfs.get(league);
      const fairs = entry?.books.get(app);
      if (entry === undefined || fairs === undefined) continue;
      posted = true;
      const need = needOf(app, league);
      if (need === null) continue;
      counted = true;
      count += fairs.filter((fair) => fair * 100 >= need).length;
      atLeast ||= need < entry.floor * 100;
    }
    return [app, counted ? { count, atLeast } : posted ? "ungraded" : null] as const;
  }));
}

export function activeFilterCount(state: OptimizerQueryState, showWeightsFilter: boolean, showMinEV = false): number {
  return getActiveAdvancedFilterCount(state, showWeightsFilter, true, showMinEV);
}

export function sideLabel(row: DisplayPlayerProp): "Over" | "Under" {
  return (row.bet_type ?? "OVER").toUpperCase() === "UNDER" ? "Under" : "Over";
}

export type PlayLine = { readonly side: "Over" | "Under"; readonly line: string; readonly was: string | null; readonly market: string };

export function playLine(row: DisplayPlayerProp): PlayLine {
  const was = row.promo?.originalLine ?? null;
  return {
    side: sideLabel(row),
    line: String(row.line),
    was: was === null || was === row.line ? null : String(was),
    market: row.market_display ?? row.stat_type,
  };
}

export function playText(play: PlayLine): string {
  return `${play.side} ${play.line}${play.was === null ? "" : ` (was ${play.was})`} · ${play.market}`;
}

export type RowLines = { readonly kind: "player" | "game"; readonly matchup: string; readonly time: string; readonly title: string; readonly play: PlayLine; readonly detail: string };

export function matchupOf(event: EventHeader): string {
  if (!event.away || !event.home) return event.name;
  return event.awayShort !== null && event.homeShort !== null ? `${event.awayShort} @ ${event.homeShort}` : `${event.away} @ ${event.home}`;
}

export function rowLines(row: DisplayPlayerProp, event: EventHeader | undefined): RowLines {
  const game = row.market_type === "game";
  const play = playLine(row);
  return {
    kind: game ? "game" : "player",
    matchup: event ? matchupOf(event) : row.event_short ?? row.event_name ?? "",
    time: event ? dayClock(event.startMs) : row.game_start_time ?? "",
    title: game ? row.selection_label ?? row.player_name : row.player_name,
    play,
    detail: game ? play.market : playText(play),
  };
}
